import type {
  RoundRobinBracketPreviewPayload,
  RoundRobinBracketPreviewSlot,
  RoundRobinQualificationPreviewPayload,
} from "./api";
import type { BracketRound } from "./bracketLayout";

// Draw · preview de playoffs (Eduardo 2026-10-02): «mejor que diga 1er lugar
// de grupo, 2do lugar en el bracket antes de poner nombres porque se va a
// confundir el admin de que ya están programados los playoffs aún cuando no
// ha empezado el torneo». Sin resultados de grupos, los clasificados son una
// proyección (el orden de salida): el preview muestra el LUGAR que clasifica
// («1.º Grupo 1», «Mejor 3.º») en vez del nombre. Con resultados, los nombres.

type QualifierRow = RoundRobinQualificationPreviewPayload["qualifiers"][number];

/** Aviso de una línea junto al preview mientras no hay resultados de grupos. */
export const PLAYOFF_NAMES_PENDING_NOTE = "Los nombres aparecen cuando haya resultados de grupos.";

/** Aviso de los seeds manuales mientras no hay resultados de grupos. */
export const PLAYOFF_SEEDS_PENDING_NOTE = "Los seeds manuales se ajustan cuando haya resultados de grupos.";

/** ¿Por qué no se pueden editar los seeds manuales? (null = sí se puede).
 * Un seed manual se guarda por JUGADOR (participant_id), no por lugar: sin
 * resultados de grupos la tabla dice «1.º Grupo 2» y el admin creería que
 * siembra el lugar, pero sembraría a quien hoy va primero en la proyección
 * (si después queda 2.º, se lleva el seed). Se bloquea hasta que haya
 * resultados, igual que con resultados de playoffs. */
export function manualSeedLockReason(input: { hasPlayoffResults: boolean; namesPending: boolean }): string | null {
  if (input.hasPlayoffResults) return "Bloqueado: ya existen resultados de playoffs.";
  if (input.namesPending) return PLAYOFF_SEEDS_PENDING_NOTE;
  return null;
}

/** ¿Aún no hay resultados de grupos? (el servidor no cuenta los de playoffs). */
export function playoffNamesPending(preview: RoundRobinQualificationPreviewPayload | null | undefined): boolean {
  return Boolean(preview?.enabled) && preview?.summary?.matches_with_results === 0;
}

/** «Grupo 1» se queda; «A» → «Grupo A». */
function groupLabel(name: string | null | undefined): string {
  const clean = (name ?? "").trim();
  if (!clean) return "su grupo";
  return /^grupo\b/i.test(clean) ? clean : `Grupo ${clean}`;
}

function placeLabel(position: number | null | undefined, groupName: string | null | undefined): string {
  return position ? `${position}.º ${groupLabel(groupName)}` : `Clasificado de ${groupLabel(groupName)}`;
}

/** Lugar que clasifica a cada participante del preview: «1.º Grupo 1»,
 * «2.º Grupo 2», «Mejor 3.º» (o «Mejor 3.º (2)» si son varios),
 * «Mejor general». */
export function qualifierPlaceholderLabels(qualifiers: QualifierRow[]): Map<number, string> {
  const labels = new Map<number, string>();
  const bestRows = new Map<string, QualifierRow[]>();
  for (const row of qualifiers) {
    if (row.participant_id == null) continue;
    const rule = String(row.qualification_rule ?? "");
    if (rule === "best_n_by_group_position" || rule === "best_n_overall") {
      const key = rule === "best_n_overall" ? rule : `${rule}:${row.position ?? ""}`;
      bestRows.set(key, [...(bestRows.get(key) ?? []), row]);
      continue;
    }
    labels.set(row.participant_id, placeLabel(row.position, row.group_name));
  }
  for (const [key, rows] of bestRows) {
    rows.forEach((row, index) => {
      const base = key === "best_n_overall" ? "Mejor general" : row.position ? `Mejor ${row.position}.º` : "Mejor clasificado";
      labels.set(row.participant_id, rows.length > 1 ? `${base} (${index + 1})` : base);
    });
  }
  return labels;
}

/** Nombre de un lado del preview: el lugar mientras no hay resultados; el
 * nombre (o «BYE» / «Ganador preliminar …») en cualquier otro caso. */
export function previewSlotName(
  slot: RoundRobinBracketPreviewSlot,
  placeholders: Map<number, string> | null,
): string {
  const name = slot.short_name || slot.display_name;
  if (!placeholders || slot.source !== "qualifier") return name;
  if (slot.participant_id != null && placeholders.has(slot.participant_id)) {
    return placeholders.get(slot.participant_id) as string;
  }
  return placeLabel(slot.group_position, slot.group_name);
}

function stageLabel(matchCount: number): string {
  switch (matchCount) {
    case 1: return "Final";
    case 2: return "Semifinales";
    case 4: return "Cuartos de final";
    case 8: return "Octavos de final";
    case 16: return "Ronda de 32";
    case 32: return "Ronda de 64";
    default: return `Ronda de ${Math.max(matchCount * 2, 2)}`;
  }
}

/** Rondas del preview de bracket de playoffs para <EliminationBracket>, con
 * las rondas siguientes como «Ganador … · Partido N». Con `placeholders`,
 * los lugares en vez de los nombres. */
export function buildPlayoffPreviewRounds(
  bracketPreview: RoundRobinBracketPreviewPayload | null | undefined,
  placeholders: Map<number, string> | null = null,
): BracketRound[] {
  if (!bracketPreview || bracketPreview.rounds.length === 0) return [];
  const rounds: BracketRound[] = bracketPreview.rounds.map((round) => ({
    label: round.label,
    matches: round.matchups.map((matchup) => ({
      id: `${round.key}-${matchup.slot}`,
      top: {
        name: previewSlotName(matchup.player1, placeholders),
        seed: matchup.player1.source === "qualifier" ? matchup.player1.seed ?? null : null,
        games: [],
        isWinner: matchup.is_bye,
      },
      bottom: {
        name: previewSlotName(matchup.player2, placeholders),
        seed: matchup.player2.source === "qualifier" ? matchup.player2.seed ?? null : null,
        games: [],
        isWinner: false,
      },
      pending: matchup.player1.source !== "qualifier" || matchup.player2.source !== "qualifier",
    })),
  }));
  let previousLabel = rounds[rounds.length - 1]?.label ?? "Ronda previa";
  let matchCount = Math.ceil((rounds[rounds.length - 1]?.matches.length ?? 0) / 2);
  while (matchCount >= 1) {
    const label = stageLabel(matchCount);
    rounds.push({
      label,
      matches: Array.from({ length: matchCount }, (_, index) => ({
        id: `preview-placeholder-${label}-${index + 1}`,
        top: { name: `Ganador ${previousLabel} · Partido ${index * 2 + 1}`, seed: null, games: [], isWinner: false },
        bottom: { name: `Ganador ${previousLabel} · Partido ${index * 2 + 2}`, seed: null, games: [], isWinner: false },
        pending: true,
      })),
    });
    previousLabel = label;
    if (matchCount === 1) break;
    matchCount = Math.ceil(matchCount / 2);
  }
  return rounds;
}
